/**
 * StatusBadge — colored badge for editorial_status and priority.
 *
 * Clickable to cycle through statuses when onClick is provided.
 */

import type { EditorialStatus, Priority } from '../../types/fileManager'

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  needs_review: { label: 'Review', className: 'rq-status-chip-draft' },
  approved: { label: 'Approved', className: 'rq-status-chip-final' },
  selects: { label: 'Selects', className: 'rq-status-chip-review' },
  rejected: { label: 'Rejected', className: 'bg-red-500/20 text-red-300 border border-red-400/30' },
}

const PRIORITY_CONFIG: Record<string, { label: string; className: string }> = {
  p1: { label: 'P1', className: 'bg-red-500/15 text-red-300 border border-red-500/35' },
  p2: { label: 'P2', className: 'rq-status-chip-draft' },
  p3: { label: 'P3', className: 'rq-status-chip-review' },
}

const STATUS_CYCLE: EditorialStatus[] = ['needs_review', 'approved', 'selects', 'rejected']

interface StatusBadgeProps {
  status?: string | null
  priority?: string | null
  onClick?: (nextStatus: string) => void
  onPriorityClick?: (nextPriority: string) => void
  size?: 'sm' | 'md'
}

export default function StatusBadge({
  status,
  priority,
  onClick,
  onPriorityClick,
  size = 'sm',
}: StatusBadgeProps) {
  const sizeClass = size === 'sm' ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-1'

  const handleStatusClick = (e: React.MouseEvent) => {
    if (!onClick || !status) return
    e.stopPropagation()
    const idx = STATUS_CYCLE.indexOf(status as EditorialStatus)
    const next = STATUS_CYCLE[(idx + 1) % STATUS_CYCLE.length]
    onClick(next)
  }

  const handlePriorityClick = (e: React.MouseEvent) => {
    if (!onPriorityClick) return
    e.stopPropagation()
    const cycle: Priority[] = ['p1', 'p2', 'p3']
    const idx = priority ? cycle.indexOf(priority as Priority) : -1
    const next = cycle[(idx + 1) % cycle.length]
    onPriorityClick(next)
  }

  const sc = status ? STATUS_CONFIG[status] : null
  const pc = priority ? PRIORITY_CONFIG[priority] : null

  return (
    <div className="flex items-center gap-1">
      {sc && (
        <span
          className={`fm-status-badge rq-status-chip ${sc.className} ${sizeClass} font-medium leading-none ${onClick ? 'cursor-pointer hover:opacity-80' : ''}`}
          onClick={handleStatusClick}
          title={`Status: ${sc.label}`}
        >
          {sc.label}
        </span>
      )}
      {pc && (
        <span
          className={`fm-status-badge rq-status-chip ${pc.className} ${sizeClass} font-medium leading-none ${onPriorityClick ? 'cursor-pointer hover:opacity-80' : ''}`}
          onClick={handlePriorityClick}
          title={`Priority: ${pc.label}`}
        >
          {pc.label}
        </span>
      )}
    </div>
  )
}
